// useAfeData — localStorage demo-data CRUD for the AFE register + the AFE↔job links (M:N).
//
// AFE is promoted from scattered strings (Pad.padAfe / Well.afeNumber / Job.afe) to a first-class
// `Afe` register. One AFE funds many jobs; one job carries many AFEs — the many-to-many lives in a
// separate `AfeLink` join (afeId, jobId, jobKind=pad|well), so a pad-development AFE can fund several
// well jobs and supplementals can be added per well. This hook owns the AFE aggregate: the register
// AND its links (deleting an AFE cascades its links).
//
// CHARTER Abs-2: NO economic math here — the AFE amount and the cross-well allocation are computed in
// Python. This hook only stores identifiers, types, statuses, and relationship rows.
//
// Isolated net-new feature: localStorage store (no Redux), like usePadData. RTK Query (afeApi) is the
// "real" code path; this is the development fallback (CLAUDE.md API Strategy).

import { useCallback, useState } from "react";
import { generatePrefixedId } from "../../../lib/id";
import { createAfeRemote } from "../store/afeBudgetRemote";
import { migrateAfe } from "../migrateAfe";
import type { Afe, AfeLink, AfeJobKind, CreateAfeDTO } from "../types";

/** localStorage keys for the demo persistence path. Bump the suffix if a shape changes. */
export const AFES_STORAGE_KEY = "oxbo-afes-v1";
export const AFE_LINKS_STORAGE_KEY = "oxbo-afe-links-v1";

export interface UseAfeDataReturn {
  afes: Afe[];
  afeLinks: AfeLink[];
  // ── AFE register ──
  getAfes: () => Afe[];
  getAfeById: (id: string) => Afe | undefined;
  /** Register the AFE on the server, then locally. REJECTS if the server refuses —
   *  a header that does not exist server-side cannot hold a budget. */
  createAfe: (data: CreateAfeDTO) => Promise<Afe>;
  /** Run work; on a throw, restore the AFE register to the bytes it held before. */
  withRegisterRollback: <T>(work: () => T) => T;
  updateAfe: (afe: Afe) => void;
  /** Delete an AFE and CASCADE-remove every link that referenced it (no dangling links). */
  deleteAfe: (id: string) => void;
  // ── AFE ↔ job links (M:N) ──
  /** Link an AFE to a job; idempotent — returns the existing link if one already matches. */
  linkAfeToJob: (afeId: string, jobId: string, jobKind: AfeJobKind) => AfeLink;
  unlinkAfe: (linkId: string) => void;
  /** Resolve the AFEs funding a given job (via the links). */
  getAfesForJob: (jobId: string, jobKind: AfeJobKind) => Afe[];
  /** The links a given AFE participates in (which jobs it funds). */
  getLinksForAfe: (afeId: string) => AfeLink[];
}

function readJSON<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

/** Name the ACTUAL failure, rather than assuming one (AFE-15 F2).
 *
 *  `localStorage.setItem` refuses for reasons that need different responses and that this app had
 *  no way to tell apart: `QuotaExceededError` means free up space, `SecurityError` means storage is
 *  blocked or partitioned and freeing space will not help, and a `TypeError` out of
 *  `JSON.stringify` is a defect in the record being written and no amount of space fixes it.
 *
 *  All three used to render as one fixed sentence asserting the browser was "full or unavailable",
 *  so a failure reported in session on 2026-09-04 could not be diagnosed at all. That report has
 *  NO LEDGER ENTRY — `PLAN_afe15_write_durability.md` §10 flags it, and marks "the reported
 *  failure was a quota error" as NOT ESTABLISHED. Quota is a hypothesis; this is the code that
 *  will settle it. DOM exceptions carry the discriminator in `.name`; this surfaces it.
 *  Pure formatting, no I/O. */
export function describeStorageFailure(cause: unknown): string {
  if (cause instanceof DOMException) return cause.name;
  if (cause instanceof Error) {
    return cause.name && cause.name !== "Error"
      ? `${cause.name}: ${cause.message}`
      : cause.message || "Error";
  }
  return typeof cause === "string" && cause ? cause : "cause not reported";
}

/** Thrown when a failed write could not be UNDONE — the original failure plus the restore's own.
 *  Its existence is the point: "we cleaned up" is a claim, and a caller may only make it when the
 *  cleanup actually ran. */
export class AfeRollbackError extends Error {
  constructor(
    readonly originalCause: unknown,
    readonly restoreCause: unknown,
  ) {
    super("The AFE write failed and could not be rolled back.");
    this.name = "AfeRollbackError";
  }
}

/** Thrown when a register write does not land. Carries the key so the caller can say WHICH store
 *  failed. See writeJSON — this replaced a silent no-op.
 *
 *  The message NAMES THE CAUSE and no longer diagnoses it. It previously ended "Browser storage is
 *  full or unavailable", which is a guess: `AfeStartPage` rendered `err.message` alone and
 *  discarded `cause`, so every distinct failure produced the identical string (AFE-15 F2). */
export class AfeStorageError extends Error {
  constructor(
    readonly storageKey: string,
    readonly cause?: unknown,
  ) {
    super(
      `Could not save to "${storageKey}" (${describeStorageFailure(cause)}).`,
    );
    this.name = "AfeStorageError";
  }
}

// THROWS on failure — it must not swallow. This previously no-opped on a caught error, which meant
// a quota-exceeded or private-mode write silently vanished while createAfe still returned a
// well-formed Afe object. The caller navigated to /afes/<id>/edit with an id that was never
// persisted, and the Builder rendered "AFE not found" with nothing anywhere explaining why.
// Losing a money record without saying so is exactly what Charter Abs-10 forbids.
function writeJSON<T>(key: string, value: T[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    throw new AfeStorageError(key, err);
  }
}

// Read-time migration (RM-14): normalize legacy capture-only records (currency default, legacy
// afeStatus → typed status; afeClass left unset). Pure + idempotent — migrateAfe never writes; the
// normalized shape is persisted only on the next mutation below (lossless — legacy afeStatus kept).
const readAfes = (): Afe[] => readJSON<Afe>(AFES_STORAGE_KEY).map(migrateAfe);
const readLinks = (): AfeLink[] => readJSON<AfeLink>(AFE_LINKS_STORAGE_KEY);

export function useAfeData(): UseAfeDataReturn {
  const [afes, setAfes] = useState<Afe[]>(() => readAfes());
  const [afeLinks, setAfeLinks] = useState<AfeLink[]>(() => readLinks());

  const persistAfes = useCallback((next: Afe[]) => {
    writeJSON(AFES_STORAGE_KEY, next);
    setAfes(next);
  }, []);

  const persistLinks = useCallback((next: AfeLink[]) => {
    writeJSON(AFE_LINKS_STORAGE_KEY, next);
    setAfeLinks(next);
  }, []);

  // ── AFE register ──────────────────────────────────────────────────────────
  const getAfes = useCallback((): Afe[] => readAfes(), []);

  const getAfeById = useCallback(
    (id: string): Afe | undefined => readAfes().find((a) => a.id === id),
    [],
  );

  /** Run `work`; if it throws, put the AFE register back to the EXACT BYTES it held beforehand.
   *
   *  WHY THIS AND NOT A DELETE (AFE-15 F2, after red team). The register and the budget are two
   *  stores with no transaction between them, so a header can land while its lines do not. The
   *  obvious undo — call `deleteAfe` on the header — is wrong here twice over:
   *
   *    * `PLAN_afe15_write_durability.md` §1 rules it out in as many words: _"Not a purge …
   *      Nothing here deletes a money record"_, and §6 adds that a disposition, if ever wanted,
   *      _"is `archivedAt`, never a delete"_.
   *    * In this repo **Abs-7 IS the archive-not-delete rule** for AFEs — `AfeStartPage` says so
   *      itself, `"Abs-7 (archive, never delete)"`. A first draft of this fix cited Abs-7 as
   *      LICENCE for the delete, which inverts it. `deleteAfe`'s own comment also calls it the
   *      dev-only hard-delete path that the UI does not call, and this would have made the UI its
   *      first production caller.
   *
   *  Restoring the prior bytes is not a deletion at all. Nothing that ever completed is removed:
   *  a half-finished create is unwound to the state before it started, which is what Abs-7 asks
   *  for — no silent destruction — rather than what it forbids. It also restores byte-for-byte
   *  through `migrateAfe`'s read-time normalization, which a filter-and-rewrite cannot.
   *
   *  The restore can ITSELF fail; that is reported, never swallowed, so no caller can claim a
   *  cleanup that did not happen. */
  const withRegisterRollback = useCallback(<T>(work: () => T): T => {
    let before: string | null = null;
    try {
      before = localStorage.getItem(AFES_STORAGE_KEY);
    } catch {
      // Storage unreadable. Run anyway — but a rollback cannot be honest, so say so if asked.
      return work();
    }
    // The restore, factored out because it now has TWO call sites — a synchronous throw
    // and a rejected promise — and they must unwind identically.
    const restore = (err: unknown): never => {
      try {
        if (before === null) localStorage.removeItem(AFES_STORAGE_KEY);
        else localStorage.setItem(AFES_STORAGE_KEY, before);
      } catch (restoreErr) {
        throw new AfeRollbackError(err, restoreErr);
      }
      throw err;
    };

    let result: T;
    try {
      result = work();
    } catch (err) {
      return restore(err);
    }

    // ASYNC WORK UNWINDS TOO. Once the budget write is a network call, `work` returns a
    // promise and a synchronous catch cannot see its rejection — the register would keep
    // a header whose lines never landed, which is the exact half-written state this
    // wrapper exists to prevent. A thenable check, not instanceof Promise: RTK Query
    // returns a promise-like. Synchronous work is untouched and still unwinds above.
    if (typeof (result as PromiseLike<unknown> | null)?.then === "function") {
      return (result as PromiseLike<unknown>).then(
        (v) => v,
        (err: unknown) => restore(err),
      ) as unknown as T;
    }
    return result;
  }, []);

  const createAfe = useCallback(
    async (data: CreateAfeDTO): Promise<Afe> => {
      const now = new Date().toISOString();
      const afe: Afe = {
        ...data,
        id: generatePrefixedId("afe"),
        createdAt: now,
        modifiedAt: now,
      };

      // SERVER FIRST, and the ORDER is the fix (adversarial review 2026-09-07, SEV-0).
      //
      // The budget store moved to Postgres and this did not, so nothing ever called
      // POST /api/afes and every budget write for an app-minted AFE hit a 404. A 404 is an
      // ANSWER, so the budget store correctly refused to fall back, and the rollback then
      // unwound the create — the app only worked while the server was DOWN.
      //
      // Registering BEFORE the local write means a server refusal leaves nothing written
      // locally to unwind. An unreachable server returns silently and the local path
      // proceeds, which is the same unreachable-vs-answered policy the budget write uses.
      await createAfeRemote({
        ...afe,
        name: afe.name,
        afeType: afe.afeType,
      });

      const current = readAfes();
      persistAfes([...current, afe]);
      // READ-BACK VERIFICATION. A write that did not throw is still not proof the record is
      // RETRIEVABLE — storage can accept a write and fail to return it (quota eviction, a
      // concurrent overwrite from another tab, a seeder replacing the key). Callers navigate
      // straight to the new AFE, so confirm it can be found before handing back an id that would
      // render "AFE not found" with no explanation (Charter Abs-10: no unverified success).
      if (!readAfes().some((a) => a.id === afe.id)) {
        throw new AfeStorageError(AFES_STORAGE_KEY);
      }
      return afe;
    },
    [persistAfes],
  );

  const updateAfe = useCallback(
    (afe: Afe): void => {
      const next = readAfes().map((a) =>
        a.id === afe.id ? { ...afe, modifiedAt: new Date().toISOString() } : a,
      );
      persistAfes(next);
    },
    [persistAfes],
  );

  const deleteAfe = useCallback(
    (id: string): void => {
      persistAfes(readAfes().filter((a) => a.id !== id));
      // Cascade: drop every link that referenced the deleted AFE so none dangle. NOTE: two separate
      // localStorage writes (afes, then links) — non-atomic on the demo path; the real backend does
      // this in ONE transaction. A swallowed second write would orphan links (acceptably unlikely in
      // the synchronous demo). The two writes target disjoint keys, so no read-after-write hazard.
      // BUDGET CASCADE — DEFERRED (RM-14 S1): the AfeBudget (oxbo-afe-budgets-v1, owned by
      // useAfeBudgetData) is a SEPARATE store and is NOT cascaded here — no live consumer yet. The DDL
      // declares AFELines ON DELETE CASCADE, so the real backend handles it in one transaction; on the
      // demo path a budget could orphan until the archive-detach flow (S6) / the budget consumer (S4/S5)
      // owns the cleanup. Per the archive-not-delete convention (PO 2026-06-30), deleteAfe is the
      // dev-only hard-delete path — the UI archives.
      const remainingLinks = readLinks().filter((l) => l.afeId !== id);
      persistLinks(remainingLinks);
    },
    [persistAfes, persistLinks],
  );

  // ── AFE ↔ job links (M:N) ───────────────────────────────────────────────
  const linkAfeToJob = useCallback(
    (afeId: string, jobId: string, jobKind: AfeJobKind): AfeLink => {
      const current = readLinks();
      const existing = current.find(
        (l) => l.afeId === afeId && l.jobId === jobId && l.jobKind === jobKind,
      );
      if (existing) return existing; // idempotent — never duplicate a link
      const link: AfeLink = {
        id: generatePrefixedId("afelink"),
        afeId,
        jobId,
        jobKind,
      };
      persistLinks([...current, link]);
      return link;
    },
    [persistLinks],
  );

  const unlinkAfe = useCallback(
    (linkId: string): void => {
      persistLinks(readLinks().filter((l) => l.id !== linkId));
    },
    [persistLinks],
  );

  const getLinksForAfe = useCallback(
    (afeId: string): AfeLink[] => readLinks().filter((l) => l.afeId === afeId),
    [],
  );

  const getAfesForJob = useCallback(
    (jobId: string, jobKind: AfeJobKind): Afe[] => {
      const afeIds = new Set(
        readLinks()
          .filter((l) => l.jobId === jobId && l.jobKind === jobKind)
          .map((l) => l.afeId),
      );
      return readAfes().filter((a) => afeIds.has(a.id));
    },
    [],
  );

  return {
    afes,
    afeLinks,
    getAfes,
    getAfeById,
    createAfe,
    withRegisterRollback,
    updateAfe,
    deleteAfe,
    linkAfeToJob,
    unlinkAfe,
    getAfesForJob,
    getLinksForAfe,
  };
}

export default useAfeData;
